// Fallback translation key used when a connectivity provider has no
// partner-specific troubleshooting steps for the "rates unavailable" state.
export const RC_RATE_CHECK_DEFAULT_STEPS_KEY = 'rc_rate_check_default_steps'

// Translation key used for the unrelated "unknown/technical issue" state,
// which is never partner-specific.
export const RC_RATE_CHECK_FAIL_STEPS_KEY = 'rc_rate_check_fail_steps'

/**
 * Builds the candidate translation key for a connectivity provider's
 * partner-specific troubleshooting steps.
 *
 * Partner-specific steps are keyed by the connectivity provider's numeric
 * partner ID (stable, unlike the provider's display name). This function is
 * a pure ID → key mapper; whether the key actually exists in the loaded
 * locale messages is checked separately (see `RateCheckIssueContent.vue`,
 * which uses vue-i18n's `te()` against this candidate key). This keeps the
 * locale files as the single source of truth for which providers have
 * custom copy — no separate list of "supported" partner IDs needs to be
 * maintained here.
 */
export function getRateCheckStepsKeyCandidate(partnerId: number): string {
  return `rc_rate_check_${partnerId}`
}

export interface ProviderExtras {
  email?: string
  websiteUrl?: string
}

// Decision: support email addresses and "how-to" links are hardcoded here
// for now since they're rarely-changing support contact/link details, and
// there was no immediate need for a backend-managed config. If they turn
// out to need frequent updates, move this to a backend-managed config
// instead of maintaining it in the frontend.
export const RC_PROVIDER_EXTRAS: Record<number, ProviderExtras> = {
  1602: {
    // Wubook
    email: 'italy@wubook.net',
    websiteUrl:
      'https://kb.wubook.net/help/en-gb/175-zak-channel-manager-ota-and-metasearch/436-zak-trivago-metasearch',
  },
  1672: {
    // HotelNetSolutions
    email: 'sales@hotelnetsolutions.de',
  },
  2706: {
    // Bookingexpert
    email: 'assistenza.begain@zucchetti.it',
  },
  3298: {
    // Beddy
    email: 'support@beddy.io',
  },
  1594: {
    // VerticalBooking
    email: 'support.vb@zucchetti.it',
  },
  2040: {
    // VikBooking
    websiteUrl: 'https://e4jconnect.com/support-e4jconnect/vik-channel-manager-documentation#trivago',
  },
  2464: {
    // Misterbooking
    email: 'support-hotel-misterbookinghotels@septeo.com',
  },
  3672: {
    // SoftTec
    email: 'vertrieb@softtec.de',
  },
  2400: {
    // HSystem
    email: 'suporte@hsystem.com.br',
    websiteUrl: 'https://hbook.hsystem.com.br/Account/Login',
  },
  2196: {
    // Octorate
    email: 'contact@octorate.com',
    websiteUrl: 'https://community.octorate.com/articles-u84kjj5k/post/come-collegare-trivago-QAHecer6kErCwv3',
  },
  1596: {
    // Vioma
    email: 'helpdesk@vioma.de',
    websiteUrl: 'https://vioma-gmbh.atlassian.net/wiki/spaces/CSTPRO/pages/3069575192/Trivago',
  },
  1794: {
    // Beds24
    websiteUrl: 'https://wiki.beds24.com/index.php/Trivago',
  },
}
